import { Tag } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Popover } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import styled from 'styled-components'
import type { PayReportRow, PayReportSortField } from '../types/payReport'
import { AbsencePopoverBody } from './AbsencePopoverBody'
import { PayReportTeamMemberCell } from './PayReportTeamMemberCell'

const NeedsAttentionTagWrap = styled.span`
  cursor: pointer;
`

const AbsentHoverText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  cursor: pointer;
`

const CellPrimaryText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const HeaderText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const MultilineHeaderText = styled(Text)`
  white-space: pre-line;
  color: ${({ theme }) => theme.color.neutral.color3};
`

type Translate = (key: string, options?: Record<string, unknown>) => string

export type GetPayReportColumnsParams = {
  t: Translate
  sortField: PayReportSortField
  sortDir: 1 | -1
  navigateToReconciliation: (teamMemberId: string) => void
  resolveContractTypeLabel: (contractTypeId?: string | null) => string
}

export function getPayReportColumns({
  t,
  sortField,
  sortDir,
  navigateToReconciliation,
  resolveContractTypeLabel
}: GetPayReportColumnsParams): ColumnsType<PayReportRow> {
  const sortOrderFor = (key: PayReportSortField) =>
    sortField === key ? (sortDir === 1 ? 'ascend' : 'descend') : undefined

  return [
    {
      title: (
        <HeaderText weight="bold">{t('PAY_REPORT_COL_TEAM_MEMBER')}</HeaderText>
      ),
      key: 'teamMemberName',
      sortOrder: sortOrderFor('teamMemberName'),
      sorter: true,
      width: 220,
      fixed: 'left',
      render: (_, record) => (
        <PayReportTeamMemberCell
          record={record}
          t={t}
          resolveContractTypeLabel={resolveContractTypeLabel}
        />
      )
    },
    {
      title: (
        <MultilineHeaderText weight="medium">
          {t('PAY_REPORT_COL_TOTAL_RECONCILED_PAY')}
        </MultilineHeaderText>
      ),
      key: 'totalReconciledPay',
      sortOrder: sortOrderFor('totalReconciledPay'),
      sorter: true,
      onHeaderCell: () => ({
        className: 'reconciliation-header-multiline'
      }),
      width: 160,
      render: (_, record) => (
        <CellPrimaryText weight="bold">
          {record.totalReconciledPay} {t('RECONCILIATION_GBP_SUFFIX')}
        </CellPrimaryText>
      )
    },
    {
      title: (
        <MultilineHeaderText weight="medium">
          {t('PAY_REPORT_COL_NEEDS_ATTENTION')}
        </MultilineHeaderText>
      ),
      key: 'needsAttentionCount',
      sortOrder: sortOrderFor('needsAttentionCount'),
      sorter: true,
      onHeaderCell: () => ({
        className: 'reconciliation-header-multiline'
      }),
      width: 160,
      render: (_, record) =>
        record.needsAttentionCount === 0 ? (
          <Tag color="success">{t('PAY_REPORT_NEEDS_ATTENTION_ALL_OK')}</Tag>
        ) : (
          <NeedsAttentionTagWrap
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                navigateToReconciliation(record.teamMemberId)
              }
            }}
            onClick={(e) => {
              e.stopPropagation()
              navigateToReconciliation(record.teamMemberId)
            }}
          >
            <Tag color="orange">
              {t('PAY_REPORT_NEEDS_ATTENTION_EVENTS', {
                count: record.needsAttentionCount
              })}
            </Tag>
          </NeedsAttentionTagWrap>
        )
    },
    {
      title: (
        <MultilineHeaderText weight="medium">
          {t('PAY_REPORT_COL_RECONCILED_HOURS')}
        </MultilineHeaderText>
      ),
      key: 'reconciledHours',
      sortOrder: sortOrderFor('reconciledHours'),
      sorter: true,
      onHeaderCell: () => ({
        className: 'reconciliation-header-multiline'
      }),
      width: 140,
      render: (_, record) => (
        <CellPrimaryText>{record.reconciledHours}</CellPrimaryText>
      )
    },
    {
      title: (
        <HeaderText weight="medium">
          {t('PAY_REPORT_COL_RECONCILED_PAY')}
        </HeaderText>
      ),
      key: 'reconciledPay',
      sortOrder: sortOrderFor('reconciledPay'),
      sorter: true,
      width: 140,
      render: (_, record) => (
        <CellPrimaryText>
          {record.reconciledPay} {t('RECONCILIATION_GBP_SUFFIX')}
        </CellPrimaryText>
      )
    },
    {
      title: (
        <MultilineHeaderText weight="medium">
          {t('PAY_REPORT_COL_TOTAL_TRAVEL_PAY')}
        </MultilineHeaderText>
      ),
      key: 'totalTravelPay',
      sortOrder: sortOrderFor('totalTravelPay'),
      sorter: true,
      onHeaderCell: () => ({
        className: 'reconciliation-header-multiline'
      }),
      width: 160,
      render: (_, record) => (
        <CellPrimaryText>
          {record.totalTravelPay} {t('RECONCILIATION_GBP_SUFFIX')}
        </CellPrimaryText>
      )
    },
    {
      title: (
        <MultilineHeaderText weight="medium">
          {t('PAY_REPORT_COL_TOTAL_MILEAGE_PAY')}
        </MultilineHeaderText>
      ),
      key: 'totalMileagePay',
      sortOrder: sortOrderFor('totalMileagePay'),
      sorter: true,
      onHeaderCell: () => ({
        className: 'reconciliation-header-multiline'
      }),
      width: 160,
      render: (_, record) => (
        <CellPrimaryText>
          {record.totalMileagePay ?? '0.00'} {t('RECONCILIATION_GBP_SUFFIX')}
        </CellPrimaryText>
      )
    },
    {
      title: (
        <MultilineHeaderText weight="medium">
          {t('PAY_REPORT_COL_ABSENT_DAYS')}
        </MultilineHeaderText>
      ),
      key: 'absentDays',
      onHeaderCell: () => ({
        className: 'reconciliation-header-multiline'
      }),
      width: 120,
      render: (_, record) => {
        const displayVal =
          record.absentDays != null ? String(record.absentDays) : '-'
        const showAbsencePopover =
          (record.absenceDaysByType?.length ?? 0) > 0 ||
          (record.absentDays != null && record.absentDays > 0)
        if (showAbsencePopover) {
          return (
            <Popover
              trigger="hover"
              placement="topLeft"
              mouseEnterDelay={0.2}
              content={<AbsencePopoverBody record={record} t={t} />}
            >
              <AbsentHoverText>{displayVal}</AbsentHoverText>
            </Popover>
          )
        }
        return <CellPrimaryText>{displayVal}</CellPrimaryText>
      }
    }
  ]
}
